import { unitTypeOptions } from '@web/entities/measurement/model/constants/unit-options.constant'
import { EMeasurementType } from '@web/entities/measurement/model/enums/measurement-type.enum'
import { EUnitType } from '@web/entities/measurement/model/enums/unit.enum'
import { EPhysicalType } from '@web/entities/point'

export const getUnitOptions = (
  physicalQuantityType?: EPhysicalType,
  types?: EMeasurementType | EMeasurementType[]
) => {
  if (!physicalQuantityType) return null

  const options = unitTypeOptions.filter((unit) => {
    const supportsPhysical =
      unit.supported.physicalQuantityType.includes(physicalQuantityType)

    let supportsType = true
    if (types) {
      if (Array.isArray(types)) {
        supportsType = types.some((t) =>
          unit.supported.typeMeasurement.includes(t)
        )
      } else {
        supportsType = unit.supported.typeMeasurement.includes(types)
      }
    }

    return supportsPhysical && supportsType
  })

  return options.map((unit) => unit.option)
}

const ALL_VIBRATION_PHYSICALS = [
  EPhysicalType.VIBRO_ACCELERATION,
  EPhysicalType.VIBRO_VELOCITY,
  EPhysicalType.VIBRO_DISPLACEMENT
]

const ALL_VIBRATION_MEASUREMENTS = [
  EMeasurementType.SIGNAL,
  EMeasurementType.SPECTER,
  EMeasurementType.OCTAVE,
  EMeasurementType.COMMON_LEVEL
]

export const shouldLoadAllVibration = (
  physicalQuantityType: EPhysicalType,
  typeMeasurement: EMeasurementType
): boolean =>
  ALL_VIBRATION_PHYSICALS.includes(physicalQuantityType) &&
  ALL_VIBRATION_MEASUREMENTS.includes(typeMeasurement)

export const getPhysicalQuantityByUnit = (unit: EUnitType) => {
  const map: Record<EUnitType, EPhysicalType> = {
    [EUnitType.MS2]: EPhysicalType.VIBRO_ACCELERATION,
    [EUnitType.G]: EPhysicalType.VIBRO_ACCELERATION,
    [EUnitType.MMS2]: EPhysicalType.VIBRO_ACCELERATION,

    [EUnitType.MMS]: EPhysicalType.VIBRO_VELOCITY,
    [EUnitType.MS]: EPhysicalType.VIBRO_VELOCITY,

    [EUnitType.MKM]: EPhysicalType.VIBRO_DISPLACEMENT,
    [EUnitType.MM]: EPhysicalType.VIBRO_DISPLACEMENT,
    [EUnitType.M]: EPhysicalType.VIBRO_DISPLACEMENT,

    [EUnitType.A]: EPhysicalType.AMPERAGE,
    [EUnitType.MA]: EPhysicalType.AMPERAGE,
    [EUnitType.MKA]: EPhysicalType.AMPERAGE,

    [EUnitType.VOLT]: EPhysicalType.VOLTAGE,
    [EUnitType.M_VOLT]: EPhysicalType.VOLTAGE,
    [EUnitType.U_VOLT]: EPhysicalType.VOLTAGE,

    [EUnitType.DEGREES]: EPhysicalType.TEMPERATURE,
    [EUnitType.DEGREES_CELSIUS]: EPhysicalType.TEMPERATURE,
    [EUnitType.DEGREES_FAHRENHEIT]: EPhysicalType.TEMPERATURE,
    [EUnitType.DEGREES_KELVIN]: EPhysicalType.TEMPERATURE,

    [EUnitType.HZ]: EPhysicalType.RPM,
    [EUnitType.ROTATION_COUNT_IN_MINUTE]: EPhysicalType.RPM,
    [EUnitType.SECONDS_PER_ROTATION]: EPhysicalType.RPM,

    [EUnitType.D_HZ]: EPhysicalType.RPM, // DRPM не доступно на данный момент
    [EUnitType.D_ROTATION_COUNT_IN_MINUTE]: EPhysicalType.RPM // DRPM не доступно на данный момент
  }

  return map[unit]
}

export const isUnitValueValid = (
  sourceUnit: EUnitType,
  chartTargetUnit: EUnitType | null,
  typeMeasurement?: EMeasurementType,
  isAllVibration?: boolean
): chartTargetUnit is EUnitType => {
  if (!chartTargetUnit) return false

  const unitOptions = (() => {
    if (isAllVibration) {
      return unitTypeOptions
        .filter((o) => o.supported.isVibration === true)
        .map((o) => o.option)
    }

    if (!typeMeasurement) return null

    return getUnitOptions(
      getPhysicalQuantityByUnit(sourceUnit),
      typeMeasurement
    )
  })()

  return (
    unitOptions?.some((option) => option.value === chartTargetUnit) ?? false
  )
}
